import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { 
  GradientBorderFrame, 
  TitleWithActions,
  AdminEmptyState,
} from '@/components';
import { EmptyStateType } from './AdminEmptyState';
import { ADMIN_COLORS } from '@/constants/admin';

type ButtonType = 'add' | 'download';

interface AdminEmptyScreenLayoutProps {
  title: string;
  emptyStateType: EmptyStateType;
  onAddPress?: () => void;
  showAddButton?: boolean;
  buttonType?: ButtonType;
  children?: React.ReactNode; // For when there's actual content to show
  hasContent?: boolean; // Add flag to indicate if there's actual content vs empty state
  disableScrollView?: boolean; // Add flag to disable ScrollView when children contain VirtualizedLists
}

export const AdminEmptyScreenLayout: React.FC<AdminEmptyScreenLayoutProps> = ({
  title,
  emptyStateType,
  onAddPress,
  showAddButton = true,
  buttonType = 'add',
  children,
  hasContent = false,
  disableScrollView = false,
}) => {
  const insets = useSafeAreaInsets();
  
  return (
    <View style={styles.contentArea}>
      {/* Outer frame with gradient border */}
      <View style={styles.outerFrameContainer}>
        <GradientBorderFrame style={styles.outerFrame}>
          {/* Spacing container for inner frame */}
          <View style={styles.innerFrameSpacing}>
            {/* Inner frame with gradient border */}
            <GradientBorderFrame 
              style={styles.innerFrame} 
              innerStyle={styles.innerFrameContent}
              isInnerFrame={true}
            >
              {/* Title w/actions */}
              <TitleWithActions
                title={title}
                showAddButton={showAddButton}
                showSelector={false}
                showControlBar={false}
                onAddPress={onAddPress}
                buttonType={buttonType}
              />
              
              {/* Content area */}
              <View style={styles.listContainer}>
                {disableScrollView ? (
                  // Use plain View when ScrollView is disabled (for VirtualizedLists)
                  <View style={[styles.scrollView, hasContent ? styles.scrollContentWithData : styles.scrollContent]}>
                    {children ? (
                      // Custom content when provided
                      children
                    ) : (
                      // Empty state by default
                      <View style={styles.emptyStateContainer}>
                        <AdminEmptyState type={emptyStateType} />
                      </View>
                    )}
                  </View>
                ) : (
                  // Use ScrollView when enabled (default behavior)
                  <ScrollView 
                    style={styles.scrollView}
                    contentContainerStyle={hasContent ? styles.scrollContentWithData : styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                    scrollEnabled={children ? true : false}
                  >
                    {children ? (
                      // Custom content when provided
                      children
                    ) : (
                      // Empty state by default
                      <View style={styles.emptyStateContainer}>
                        <AdminEmptyState type={emptyStateType} />
                      </View>
                    )}
                  </ScrollView>
                )}
              </View>
            </GradientBorderFrame>
          </View>
        </GradientBorderFrame>
      </View>
      
      {/* Bottom safe area with frame background */}
      <View style={[styles.bottomSafeArea, { height: insets.bottom }]} />
    </View>
  );
};

const styles = StyleSheet.create({
  contentArea: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  bottomSafeArea: {
    backgroundColor: ADMIN_COLORS.BULLETINS.FRAME_BACKGROUND, // Frame background for bottom safe area
  },
  outerFrameContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    width: '100%', // Ensure container takes full width
  },
  outerFrame: {
    // Outer frame should extend full width and height
    width: '100%',
    flex: 1, // Take all available space to bottom of screen
    alignSelf: 'stretch', // Ensure it stretches to full width
  },
  innerFrameSpacing: {
    // Create spacing around inner frame
    flex: 1,
    padding: 2, // Reduced gap between outer and inner frames
  },
  innerFrame: {
    // Inner frame should be smaller than outer frame
    flex: 1,
  },
  innerFrameContent: {
    gap: 20,
    paddingTop: 20,
    paddingHorizontal: 20,
    flex: 1,
  },
  listContainer: {
    width: '100%',
    flex: 1, // Take remaining space after title
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center', // Center vertically
    alignItems: 'center',
    paddingTop: 0, // Remove extra padding
    marginTop: -50, // Move content up slightly to match Figma
  },
  scrollContentWithData: {
    flexGrow: 1,
    paddingTop: 0, // No centering for data content
    paddingBottom: 0, 
  },
  emptyStateContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
